import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Briefcase, GraduationCap, Users, Eye, EyeOff } from "lucide-react";
import API from "@/utils/api";

const Register = () => {
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get("type") || "woman";
  const navigate = useNavigate();
  const [userType, setUserType] = useState(defaultType);
  const [formData, setFormData] = useState({ 
    name: "", 
    email: "", 
    password: "", 
    phone: "",
    address: "",
    district: ""
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const districts = [
    "Headquarter",
    "West Garo Hills",
    "East Garo Hills",
    "North Garo Hills",
    "South Garo Hills",
    "South West Garo Hills"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!formData.name || !formData.phone || !formData.password || !formData.email || !formData.address || !formData.district) {
      setError("Please fill in all required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    if (formData.password.length < 4) {
      setError("Password must be at least 4 characters");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        phone: formData.phone,
        password: formData.password,
        email: formData.email,
        address: formData.address,
        district: formData.district,
        user_type: userType
      });
      
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify({...response.data.user, user_type: userType}));
      
      if (userType === "girl") {
        navigate("/enrollment");
      } else if (userType === "woman") {
        navigate("/dream-option");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center p-6" data-testid="register-page">
      <Card className="w-full max-w-md rounded-3xl shadow-xl">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <img src="/images/logo.png" alt="NokMechik" className="w-8 h-8 rounded-full object-cover" />
            <span className="text-2xl font-bold text-slate-800">NokMechik</span>
          </Link>
          <CardTitle className="text-2xl">Create Your Account</CardTitle>
          <CardDescription>Join our community of empowered women</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6">
            <p className="text-sm text-slate-500 text-center mb-3">Select your category</p>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setUserType("woman")}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === "woman" 
                    ? "border-terracotta bg-terracotta text-white shadow-lg shadow-terracotta/30" 
                    : "border-slate-200 bg-white hover:border-terracotta/50"
                }`}
                data-testid="register-woman-tab"
              >
                <Briefcase className={`w-6 h-6 mx-auto mb-1 ${userType === "woman" ? "text-white" : "text-terracotta"}`} />
                <span className="text-xs font-medium block">Educated</span>
                <span className="text-xs font-medium block">Unemployed</span>
              </button>
              <button
                type="button"
                onClick={() => setUserType("girl")}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === "girl" 
                    ? "border-teal-600 bg-teal-600 text-white shadow-lg shadow-teal-600/30" 
                    : "border-slate-200 bg-white hover:border-teal-500/50"
                }`}
                data-testid="register-girl-tab"
              >
                <GraduationCap className={`w-6 h-6 mx-auto mb-1 ${userType === "girl" ? "text-white" : "text-teal-600"}`} />
                <span className="text-xs font-medium block">Dropout</span>
                <span className="text-xs font-medium block">Student</span>
              </button>
              <button
                type="button"
                onClick={() => setUserType("general")}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === "general" 
                    ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-600/30" 
                    : "border-slate-200 bg-white hover:border-purple-500/50"
                }`}
                data-testid="register-general-tab"
              >
                <Users className={`w-6 h-6 mx-auto mb-1 ${userType === "general" ? "text-white" : "text-purple-600"}`} />
                <span className="text-xs font-medium block">General</span>
                <span className="text-xs font-medium block">Member</span>
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl"
                required
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="rounded-xl"
                required
                data-testid="register-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="reg-email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-xl"
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-address">Address <span className="text-red-500">*</span></Label>
              <Input
                id="reg-address"
                placeholder="Enter your full address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="rounded-xl"
                required
                data-testid="register-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-district">District <span className="text-red-500">*</span></Label>
              <select
                id="reg-district"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                className="w-full p-3 border rounded-xl bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
                data-testid="register-district-select"
              >
                <option value="">Select your district</option>
                {districts.map((district) => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-password">Password <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input
                  id="reg-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="rounded-xl pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className={`w-full rounded-full py-6 ${
                userType === "woman" 
                  ? "bg-terracotta hover:bg-terracotta/90" 
                  : userType === "general"
                    ? "bg-purple-600 hover:bg-purple-700"
                    : "bg-teal-600 hover:bg-teal-700"
              }`}
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account..." : `Register as ${userType === "woman" ? "Educated Unemployed" : userType === "general" ? "General Member" : "Dropout"}`}
            </Button>
          </form>
          <p className="text-center mt-6 text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-teal-600 hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
